<script>
  // Шторка «Поделиться чатом / Участники» (этап 7.1, семейные чаты).
  // Владелец: состав чата (с «Убрать»), выбор новых участников из ростера,
  // «с этого момента», предупреждение о видимости истории. Участник: только состав.
  import { createEventDispatcher } from 'svelte';
  import BottomSheet from './BottomSheet.svelte';
  import { api } from '../api.js';
  import { TEXT } from '../ui-text.js';
  import { createArm } from '../arm.js';

  // В13 (1.27.8): «Убрать» — вторым касанием. Вернуть участника без потерь нельзя:
  // граница «с этого момента» при новом приглашении была бы другой.
  const { armed: removeArmed, tap: tapRemove } = createArm();

  export let open = false;
  export let chatId = null;
  // [{userId, role}] — текущий состав чата.
  export let members = [];
  export let isOwner = false;
  // map userId -> {userId, displayName, avatarDataUrl} (usersStore).
  export let roster = {};
  export let myUserId = null;

  const dispatch = createEventDispatcher();

  let selected = {};
  let fromNow = false;
  let busy = false;
  let errorText = '';

  $: if (!open) {
    selected = {};
    fromNow = false;
    errorText = '';
  }

  $: memberIds = new Set(members.map((member) => member.userId));
  $: candidates = Object.values(roster).filter((user) => !memberIds.has(user.userId));
  $: chosenIds = Object.keys(selected).filter((userId) => selected[userId]);
  $: sheetTitle = isOwner ? TEXT.family.shareTitle : TEXT.family.membersTitle;

  function displayName(userId) {
    const user = roster[userId];
    return user?.displayName || userId;
  }

  function avatarOf(userId) {
    return roster[userId]?.avatarDataUrl || null;
  }

  function initialOf(userId) {
    return displayName(userId).slice(0, 1).toUpperCase();
  }

  async function submitShare() {
    if (!chosenIds.length || busy || !chatId) {
      return;
    }
    busy = true;
    errorText = '';
    try {
      const result = await api.shareChat(chatId, chosenIds, fromNow);
      dispatch('changed', {
        members: result.items,
        responseMode: result.responseMode,
        responseModeChanged: Boolean(result.responseModeChanged),
      });
      dispatch('close');
    } catch (error) {
      console.error('Failed to share chat', error);
      errorText = TEXT.family.shareFailed;
    } finally {
      busy = false;
    }
  }

  async function removeMember(userId) {
    if (busy || !chatId) {
      return;
    }
    busy = true;
    errorText = '';
    try {
      await api.removeChatMember(chatId, userId);
      dispatch('changed', { members: members.filter((member) => member.userId !== userId) });
    } catch (error) {
      console.error('Failed to remove chat member', error);
      errorText = TEXT.family.memberRemoveFailed;
    } finally {
      busy = false;
    }
  }
</script>

<BottomSheet {open} title={sheetTitle} on:close={() => dispatch('close')}>
  <div class="members-section">
    {#each members as member (member.userId)}
      <div class="member-row">
        {#if avatarOf(member.userId)}
          <img class="member-avatar" src={avatarOf(member.userId)} alt="" />
        {:else}
          <span class="member-avatar member-avatar-fallback">{initialOf(member.userId)}</span>
        {/if}
        <span class="member-name">
          {displayName(member.userId)}
          {#if member.userId === myUserId}<span class="member-hint">(вы)</span>{/if}
        </span>
        {#if member.role === 'owner'}
          <span class="member-role">{TEXT.family.ownerLabel}</span>
        {:else if isOwner}
          <button
            type="button"
            class="member-remove"
            class:armed={$removeArmed === member.userId}
            disabled={busy}
            on:click={() => tapRemove(member.userId) && removeMember(member.userId)}
          >
            {$removeArmed === member.userId ? TEXT.family.memberRemoveConfirm : TEXT.family.memberRemove}
          </button>
        {/if}
      </div>
    {/each}
  </div>

  {#if isOwner}
    {#if candidates.length}
      <div class="share-section">
        {#each candidates as user (user.userId)}
          <label class="candidate-row">
            <input type="checkbox" bind:checked={selected[user.userId]} disabled={busy} />
            {#if user.avatarDataUrl}
              <img class="member-avatar" src={user.avatarDataUrl} alt="" />
            {:else}
              <span class="member-avatar member-avatar-fallback">{(user.displayName || user.userId).slice(0, 1).toUpperCase()}</span>
            {/if}
            <span class="member-name">{user.displayName || user.userId}</span>
          </label>
        {/each}

        <label class="from-now-row">
          <input type="checkbox" bind:checked={fromNow} disabled={busy} />
          <span>{TEXT.family.shareFromNow}</span>
        </label>

        <p class="share-warning">{TEXT.family.shareWarning}</p>

        <button
          type="button"
          class="share-submit"
          disabled={busy || !chosenIds.length}
          on:click={submitShare}
        >
          {TEXT.family.shareSubmit}
        </button>
      </div>
    {:else}
      <p class="share-empty">{TEXT.family.shareEmptyRoster}</p>
    {/if}
  {/if}

  {#if errorText}
    <p class="share-error">{errorText}</p>
  {/if}
</BottomSheet>

<style>
  .member-remove.armed {
    color: var(--error-text);
    border-color: var(--error-text);
    font-weight: 600;
  }

  .members-section {
    padding: 0.5rem 0.25rem;
    border-bottom: 1px solid var(--border-light);
  }

  .member-row,
  .candidate-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.25rem;
    min-height: 44px;
  }

  .candidate-row input,
  .from-now-row input {
    width: 18px;
    height: 18px;
    accent-color: var(--accent-color);
  }

  .member-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--bg-tertiary);
    flex-shrink: 0;
  }

  .member-avatar-fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-secondary);
    border: 1px solid var(--border-color);
  }

  .member-name {
    flex: 1;
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .member-hint {
    color: var(--text-muted);
    font-size: 0.8125rem;
    margin-left: 0.25rem;
  }

  .member-role {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .member-remove {
    padding: 0.25rem 0.7rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: transparent;
    color: var(--error-text);
    font: inherit;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .share-section {
    padding: 0.5rem 0.25rem 0;
  }

  .from-now-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 0.25rem 0.25rem;
    color: var(--text-secondary);
    font-size: 0.9375rem;
  }

  .share-warning {
    margin: 0.35rem 0.25rem 0.75rem;
    font-size: 0.8125rem;
    color: var(--text-muted);
    line-height: 1.4;
  }

  .share-submit {
    width: 100%;
    min-height: 44px;
    border: none;
    border-radius: 12px;
    background: var(--accent-color);
    color: var(--accent-contrast, #fff);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  .share-submit:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .share-empty {
    padding: 0.75rem 0.25rem;
    color: var(--text-muted);
    font-size: 0.9375rem;
  }

  .share-error {
    padding: 0.5rem 0.25rem 0;
    color: var(--error-text);
    font-size: 0.875rem;
  }
</style>
